import { HttpClient } from '@angular/common/http'
import { Component, NgZone, OnInit } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivationStart, Router } from '@angular/router'
import { FacebookLogin } from '@capacitor-community/facebook-login'
import { App, URLOpenListenerEvent } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { environment } from '@env/environment'
import { ModalController, Platform } from '@ionic/angular/standalone'
import * as Sentry from '@sentry/capacitor'
import { AnalyticService } from '@service/analytic.service'
import { AuthService } from '@service/auth.service'
import { ForegroundService } from '@service/foreground.service'
import { HistoryService } from '@service/history.service'
import { ToastService } from '@service/toast.service'
import { UrlOpenService } from '@service/url-open.service'
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency'
import { addIcons } from 'ionicons'
import {
  addCircleOutline,
  addOutline,
  bagCheck,
  ban,
  callOutline,
  camera,
  chatbubbleEllipses,
  checkmarkSharp,
  chevronBack,
  chevronForwardCircleSharp,
  chevronUpCircle,
  close,
  closeCircleOutline,
  copyOutline,
  createOutline,
  ellipsisVerticalOutline,
  location,
  open,
  pin,
  removeCircleOutline,
  returnUpBackSharp,
  searchOutline,
  send,
  text,
  trash,
  warningSharp,
} from 'ionicons/icons'
import * as R from 'ramda'
import { debounceTime, filter } from 'rxjs/operators'
import * as semver from 'semver'
import { register } from 'timeago.js'
import { IonModule } from './shared/ion.module'

const isPushNotificationsAvailable = Capacitor.isPluginAvailable('PushNotifications')

@Component({
    selector: 'app-root',
    template: `
    <ion-app>
      <ion-router-outlet></ion-router-outlet>
    </ion-app>
  `,
    imports: [IonModule]
})
export class AppComponent implements OnInit {
  constructor(
    private router: Router,
    private zone: NgZone,
    private auth: AuthService,
    private modalController: ModalController,
    private platform: Platform,
    private http: HttpClient,
    private urlOpenService: UrlOpenService,
    private toastService: ToastService,
    private titleService: Title,
    private analyticService: AnalyticService,
    private historyService: HistoryService,
    private foregroundService: ForegroundService,
  ) {
    // Angular가 실제로 컴포넌트를 생성한 후에 notifyAppReady 호출
    // → 번들 로드 실패 시 Capgo 자동 롤백이 정상 작동함
    CapacitorUpdater.notifyAppReady()
    ;(window as any).__APP_RENDERED__ = true

    this.setIcon()
    this.setTimeago()
    this.setStatusBar()
    this.initializeApp()
    this.initializeCapgo()
    if (this.platform.is('ios')) this.initAtt()

    // use hook after platform dom ready
    FacebookLogin.initialize({ appId: environment.facebookAppId }).then(() => {
      if (this.platform.is('ios')) {
        FacebookLogin.setAutoLogAppEventsEnabled({ enabled: true })
        FacebookLogin.setAdvertiserTrackingEnabled({ enabled: true })
        FacebookLogin.setAdvertiserIDCollectionEnabled({ enabled: true })
      }
    })

    FacebookLogin.logEvent({ name: 'kiwi_app_initialized' })
  }

  /**
   * for iOS ATT
   */
  async initAtt() {
    await AppTrackingTransparency.requestPermission()
  }

  //
  // interface
  //

  async ngOnInit() {
    if (isPushNotificationsAvailable) this.initializePushNotification()
  }

  private setStatusBar() {
    if (this.platform.is('desktop')) return

    const statusBar = async () => {
      try {
        await StatusBar.setBackgroundColor({ color: '#ffffff' })
      } catch (e) {}
      StatusBar.setStyle({ style: Style.Light })
    }
    this.platform.ready().then(async () => {
      setTimeout(() => {
        SplashScreen.hide()
      }, 500)
      if (!Capacitor.isPluginAvailable('StatusBar')) {
        setTimeout(() => {
          SplashScreen.hide()
        }, 500)
        return
      }

      let i2 = 0
      const timer2 = setInterval(() => {
        if (i2 > 10) {
          clearTimeout(timer2)
          return
        }
        statusBar()
        i2++
      }, 100)

      setTimeout(() => {
        SplashScreen.hide()
      }, 700)
    })

    const onDeviceReady = async () => {
      setTimeout(async () => {
        try {
          await ((window as any).NavigationBar &&
            (window as any).NavigationBar.backgroundColorByHexString('#FFFFFF'))
        } catch (e) {
          alert(e)
        }
      }, 800)
    }
    document.addEventListener('deviceready', onDeviceReady, false)
  }

  private async initializePushNotification() {
    // high channel
    if (this.platform.is('android'))
      PushNotifications.createChannel({
        id: 'high',
        name: 'high channel',
        importance: 5,
        vibration: true,
      })

    PushNotifications.requestPermissions().then(result => {
      if (result.receive === 'granted')
        // Register with Apple / Google to receive push via APNS/FCM
        PushNotifications.register()
    })

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', async (token: Token) => {
      Sentry.setContext('main', {
        userId: this.auth.userId,
        fcmToken: token.value,
      })
      // Sentry.captureException('Test Captured Exception')
      // Sentry.nativeCrash();
      await this.auth.registerFcmToken(token.value)
    })

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (e: any) => {
      Sentry.captureException(e)
    })

    // 화면이 떠 있는 상태에서 푸쉬가 올경우 LocalNotification으로 처리
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        if (!notification) return

        const data = JSON.stringify({
          link: notification.data.link,
          tag: notification.data.tag,
        })

        LocalNotifications.schedule({
          notifications: [
            {
              id: new Date().getTime(),
              title: notification.title,
              body: notification.body,
              extra: { data },
              smallIcon: 'ic_stat_icon',
              iconColor: 'white',
              // sound: 'default', // ios에서 없으면 소리 안남
            },
          ],
        })
      },
    )

    // 포그라운드 푸쉬가 눌러졋을 경우
    LocalNotifications.addListener('localNotificationActionPerformed', async ({ notification }) => {
      try {
        const { data } = notification.extra
        this.foregroundService.pushPressed$.next(new Date())
        await this.router.navigateByUrl(JSON.parse(data).link)
      } catch (e) {
        Sentry.captureException(e)
      }
    })

    // 백그라운드 푸쉬가 눌러 졌을 경우
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async ({ notification }: ActionPerformed) => {
        try {
          await this.router.navigateByUrl(notification.data.link)
        } catch (e) {
          Sentry.captureException(e)
        }
      },
    )
  }

  private setIcon() {
    addIcons({
      chevronBack,
      addCircleOutline,
      addOutline,
      bagCheck,
      ban,
      callOutline,
      camera,
      chatbubbleEllipses,
      checkmarkSharp,
      chevronForwardCircleSharp,
      chevronUpCircle,
      closeCircleOutline,
      close,
      copyOutline,
      createOutline,
      ellipsisVerticalOutline,
      location,
      open,
      pin,
      removeCircleOutline,
      returnUpBackSharp,
      searchOutline,
      send,
      text,
      trash,
      warningSharp,
    })
  }

  private setTimeago() {
    const localeFunc = (n: number, index: number, totalSec: number): [string, string] =>
      [
        ['방금', '곧'],
        ['%s초 전', '%s초 후'],
        ['1분 전', '1분 후'],
        ['%s분 전', '%s분 후'],
        ['1시간 전', '1시간 후'],
        ['%s시간 전', '%s시간 후'],
        ['1일 전', '1일 후'],
        ['%s일 전', '%s일 후'],
        ['1주일 전', '1주일 후'],
        ['%s주일 전', '%s주일 후'],
        ['1개월 전', '1개월 후'],
        ['%s개월 전', '%s개월 후'],
        ['1년 전', '1년 후'],
        ['%s년 전', '%s년 후'],
      ][index] as [string, string]
    register('my-locale', localeFunc)
    const localeFuncSimple = (n: number, index: number, totalSec: number): [string, string] =>
      [
        ['방금', '방금'], // TODO 곧을 방금으로 변경 => 시간차가 발생하여 미래로 인식
        ['방금', '%s초 후'],
        ['방금', '1분 후'],
        ['%s분 전', '%s분 후'],
        ['1시간 전', '1시간 후'],
        ['%s시간 전', '%s시간 후'],
        ['1일 전', '1일 후'],
        ['%s일 전', '%s일 후'],
        ['1주일 전', '1주일 후'],
        ['%s주일 전', '%s주일 후'],
        ['1개월 전', '1개월 후'],
        ['%s개월 전', '%s개월 후'],
        ['1년 전', '1년 후'],
        ['%s년 전', '%s년 후'],
      ][index] as [string, string]
    register('my-locale-simple', localeFuncSimple)
  }

  private async initializeApp() {
    // 페이지 이동 이벤트
    this.router.events
      .pipe(
        filter(event => event instanceof ActivationStart),
        debounceTime(70),
      )
      .subscribe((d: ActivationStart) => {
        // 토스트 끄기
        this.toastService.dismiss()

        const title = d.snapshot.data.name
        try {
          this.historyService.add([this.historyService.tab.getSelected(), title, this.router.url])
        } catch (e) {}

        // 타이틀 변경
        if (!title) return
        this.titleService.setTitle(title)
        this.analyticService.events([`페이지_${title.replace(' ', '_')}`], { title })
      })

    // URL열림
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.modalController.dismiss()
      this.zone.run(async () => {
        // 주소에서 '/'로 시작하는 path만 분리, default '/'
        const slug = R.join(
          '',
          (event.url.match(/(?:\w+:)?\/\/[^\/]+([^?#]+)/) || [1, '/']).slice(1),
        )

        this.urlOpenService.register(slug)
      })
    })
  }

  private async initializeCapgo() {
    // this.http.get('https://httpstat.us/404?sleep=2000').subscribe(t => {
    // })

    const check = () =>
      App.getInfo()
        .then(info => {
          let ruleVersion = ''
          const { major, minor } = semver.parse(info.version)
          if (semver.gt(semver.coerce(info.version), semver.coerce('3.7')))
            ruleVersion = `${major}.${minor}`
          this.http
            .get(`${environment.versionBaseUrl}/version${ruleVersion}?_=${new Date().getTime()}`)

            .subscribe(
              async (swVersion: { version: string }) => {
                if (!swVersion || environment.version === swVersion.version) {
                  // keep going
                  this.urlOpenService.process()
                  return
                }
                this.router.navigateByUrl('/updating', { replaceUrl: true })
              },
              e => {
                this.urlOpenService.process()
              },
            )
        })
        .catch(e => {})
    // if (environment.env !== 'local') check()
    check()
    App.addListener('appStateChange', state => {
      if (!this.historyService.updatingAvailable) return
      // log
      // try update
      if (state.isActive) {
        this.historyService.log('activate', {}, 30, 'activate')
        this.historyService.active$.next({})
        check()
      }
    })
  }
}
